import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, ChevronLeft, ChevronRight, Pencil, Calendar, X } from 'lucide-react';
import { controlHorarioAPI, textoErrorApi } from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import { hoyMadrid } from '../../utils/fechas';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from '../ui/alert-dialog';

// ==================== PIEZAS COMUNES DE EMPLEADOS ====================
// Aviso inline (sustituye a alert) y diálogo de confirmación propio
// (sustituye a confirm/prompt). Los usan Empleados, Gestión de Equipo,
// la ficha del empleado y este registro horario.

// Mensaje verde o rojo que desaparece a los 4 segundos o al siguiente aviso
export const useAvisoTemporal = (duracionMs = 4000) => {
  const [aviso, setAviso] = useState(null);
  const temporizador = useRef(null);

  const limpiarAviso = useCallback(() => {
    if (temporizador.current) clearTimeout(temporizador.current);
    temporizador.current = null;
    setAviso(null);
  }, []);

  const mostrarAviso = useCallback((tipo, texto) => {
    if (temporizador.current) clearTimeout(temporizador.current);
    setAviso({ tipo, texto });
    temporizador.current = setTimeout(() => {
      setAviso(null);
      temporizador.current = null;
    }, duracionMs);
  }, [duracionMs]);

  useEffect(() => () => {
    if (temporizador.current) clearTimeout(temporizador.current);
  }, []);

  return { aviso, mostrarAviso, limpiarAviso };
};

export const AvisoInline = ({ aviso, onCerrar, className = '', testId = 'aviso-inline' }) => {
  if (!aviso?.texto) return null;
  const esError = aviso.tipo === 'error';
  return (
    <div
      role={esError ? 'alert' : 'status'}
      data-testid={testId}
      className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-2.5 text-sm whitespace-pre-line ${
        esError ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-800'
      } ${className}`}
    >
      <span>{aviso.texto}</span>
      {onCerrar && (
        <button type="button" onClick={onCerrar} className="shrink-0 p-2 -m-2 opacity-60 hover:opacity-100" title="Cerrar aviso" aria-label="Cerrar aviso">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// Diálogo de confirmación. `children` admite campos (fechas, comentario...).
// No se cierra solo al confirmar: lo cierra quien lo abre cuando termina,
// así un error puede mostrarse dentro (`error`).
export const DialogoConfirmar = ({
  abierto,
  titulo,
  descripcion,
  children,
  textoConfirmar = 'Confirmar',
  textoCancelar = 'Cancelar',
  peligro = false,
  ocupado = false,
  deshabilitado = false,
  error = '',
  onConfirmar,
  onCancelar,
  testId = 'dialogo-confirmar',
}) => (
  <AlertDialog open={!!abierto} onOpenChange={(abrir) => { if (!abrir && !ocupado) onCancelar(); }}>
    <AlertDialogContent
      className="bg-white z-[80] w-[calc(100%-2rem)] rounded-xl max-h-[85dvh] overflow-y-auto p-4 sm:p-6"
      data-testid={testId}
    >
      <AlertDialogHeader>
        <AlertDialogTitle>{titulo}</AlertDialogTitle>
        {descripcion && <AlertDialogDescription className="whitespace-pre-line">{descripcion}</AlertDialogDescription>}
      </AlertDialogHeader>
      {children && <div className="space-y-3">{children}</div>}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm whitespace-pre-line" role="alert">
          {error}
        </div>
      )}
      {/* Pie pegado abajo: con el teclado abierto los botones siguen a la vista */}
      <AlertDialogFooter className="gap-2 sticky bottom-0 bg-white pt-3">
        <button
          type="button"
          onClick={onCancelar}
          disabled={ocupado}
          className="px-4 py-2.5 sm:py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          data-testid={`${testId}-cancelar`}
        >
          {textoCancelar}
        </button>
        <button
          type="button"
          onClick={onConfirmar}
          disabled={ocupado || deshabilitado}
          className={`px-4 py-2.5 sm:py-2 text-white rounded-lg disabled:opacity-50 ${
            peligro ? 'bg-red-600 hover:bg-red-700' : 'bg-duetto-primary hover:opacity-90'
          }`}
          data-testid={`${testId}-aceptar`}
        >
          {ocupado ? 'Un momento...' : textoConfirmar}
        </button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

// ==================== REGISTRO HORARIO ====================

// Hora guardada por el servidor (UTC) a Date. Si llega sin zona se toma como UTC.
const fechaHoraServidor = (valor) => {
  if (!valor) return null;
  const texto = String(valor);
  const conZona = /([zZ]|[+-]\d{2}:?\d{2})$/.test(texto) ? texto : `${texto}Z`;
  const d = new Date(conZona);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Date -> "YYYY-MM-DDTHH:MM" en hora local (para input datetime-local)
const aInputLocal = (d) => {
  if (!d) return '';
  const dos = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}T${dos(d.getHours())}:${dos(d.getMinutes())}`;
};

// Hora corta local de un fichaje
const horaCorta = (valor) => {
  const d = fechaHoraServidor(valor);
  return d ? d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : null;
};

const DIAS_SEMANA = [
  { clave: 'lunes', letra: 'L' },
  { clave: 'martes', letra: 'M' },
  { clave: 'miercoles', letra: 'X' },
  { clave: 'jueves', letra: 'J' },
  { clave: 'viernes', letra: 'V' },
  { clave: 'sabado', letra: 'S' },
  { clave: 'domingo', letra: 'D' },
];

const FORM_VACIO = { fecha: '', hora_entrada: '', hora_salida: '', notas: '' };

/**
 * Calendario mensual de fichajes de un empleado con alta, edición y borrado
 * de fichajes manuales. Lo usan la ficha del empleado (pestaña Registro
 * horario) y Gestión de Equipo (Equipo y Horas).
 * - empleadoId: id del empleado.
 * - onCambio: se llama tras crear, editar o borrar un fichaje.
 * - soloLectura: sin alta, edición ni borrado (Mi Inicio del empleado).
 */
const RegistroHorarioEmpleado = ({ empleadoId, onCambio, soloLectura = false }) => {
  const { user } = useAuth();
  // El fichaje manual solo lo gestionan dirección y RRHH (el servidor lo exige)
  const puedeEditar = !soloLectura && ['direccion', 'rrhh'].includes(user?.rol);

  const [registros, setRegistros] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [errorCarga, setErrorCarga] = useState('');
  const [mes, setMes] = useState(() => new Date().getMonth() + 1);
  const [anio, setAnio] = useState(() => new Date().getFullYear());
  const { aviso, mostrarAviso, limpiarAviso } = useAvisoTemporal();

  // Formulario de alta/edición
  const [formAbierto, setFormAbierto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [errorForm, setErrorForm] = useState('');
  const [guardando, setGuardando] = useState(false);

  // Confirmación de borrado
  const [aBorrar, setABorrar] = useState(null);
  const [borrando, setBorrando] = useState(false);
  const [errorBorrar, setErrorBorrar] = useState('');

  const cargarRegistros = useCallback(async () => {
    if (!empleadoId) return;
    setCargando(true);
    setErrorCarga('');
    try {
      const response = await controlHorarioAPI.getRegistrosByEmpleado(empleadoId, mes, anio);
      setRegistros(response.data?.registros || []);
    } catch (error) {
      console.error('Error cargando registros horarios:', error);
      setRegistros([]);
      setErrorCarga(textoErrorApi(error.response?.data?.detail, 'No se han podido cargar los fichajes'));
    } finally {
      setCargando(false);
    }
  }, [empleadoId, mes, anio]);

  useEffect(() => {
    cargarRegistros();
  }, [cargarRegistros]);

  const cambiarMes = (direccion) => {
    let nuevoMes = mes + direccion;
    let nuevoAnio = anio;
    if (nuevoMes > 12) {
      nuevoMes = 1;
      nuevoAnio += 1;
    } else if (nuevoMes < 1) {
      nuevoMes = 12;
      nuevoAnio -= 1;
    }
    setMes(nuevoMes);
    setAnio(nuevoAnio);
  };

  const abrirFormulario = (registro = null) => {
    limpiarAviso();
    setErrorForm('');
    if (registro) {
      // Editar: el formulario muestra la hora LOCAL
      setEditando(registro);
      setForm({
        fecha: registro.fecha,
        hora_entrada: aInputLocal(fechaHoraServidor(registro.hora_entrada)),
        hora_salida: aInputLocal(fechaHoraServidor(registro.hora_salida)),
        notas: registro.notas || '',
      });
    } else {
      setEditando(null);
      setForm({ ...FORM_VACIO, fecha: hoyMadrid() });
    }
    setFormAbierto(true);
  };

  const cerrarFormulario = () => {
    if (guardando) return;
    setFormAbierto(false);
    setEditando(null);
    setErrorForm('');
  };

  const guardarFichaje = async () => {
    // Las horas del formulario son locales; viajan como ISO con zona (UTC, "Z")
    const entrada = form.hora_entrada ? new Date(form.hora_entrada) : null;
    const salida = form.hora_salida ? new Date(form.hora_salida) : null;
    if ((entrada && Number.isNaN(entrada.getTime())) || (salida && Number.isNaN(salida.getTime()))) {
      setErrorForm('Revisa las horas: alguna no es válida');
      return;
    }
    if (entrada && salida && salida <= entrada) {
      setErrorForm('La salida debe ser posterior a la entrada');
      return;
    }
    setErrorForm('');
    setGuardando(true);
    try {
      if (editando) {
        await controlHorarioAPI.editarFichajeManual(editando.id, {
          hora_entrada: entrada ? entrada.toISOString() : null,
          hora_salida: salida ? salida.toISOString() : null,
          // Cadena (vacía si se borra): con null la URL llevaría el texto "null"
          notas: form.notas || '',
        });
      } else {
        await controlHorarioAPI.crearFichajeManual({
          empleado_id: empleadoId,
          fecha: form.fecha,
          hora_entrada: entrada ? entrada.toISOString() : null,
          hora_salida: salida ? salida.toISOString() : null,
          notas: form.notas || null,
        });
      }
      const eraEdicion = !!editando;
      setFormAbierto(false);
      setEditando(null);
      await cargarRegistros();
      mostrarAviso('ok', eraEdicion ? 'Fichaje actualizado' : 'Fichaje creado');
      if (onCambio) onCambio();
    } catch (error) {
      console.error('Error guardando fichaje:', error);
      setErrorForm(textoErrorApi(error.response?.data?.detail, 'Error al guardar el fichaje'));
    } finally {
      setGuardando(false);
    }
  };

  const confirmarBorrado = async () => {
    if (!aBorrar) return;
    setBorrando(true);
    setErrorBorrar('');
    try {
      await controlHorarioAPI.eliminarFichajeManual(aBorrar.id);
      setABorrar(null);
      await cargarRegistros();
      mostrarAviso('ok', 'Fichaje eliminado');
      if (onCambio) onCambio();
    } catch (error) {
      console.error('Error eliminando fichaje:', error);
      setErrorBorrar(textoErrorApi(error.response?.data?.detail, 'Error al eliminar el fichaje'));
    } finally {
      setBorrando(false);
    }
  };

  const totalMinutos = registros.reduce((sum, r) => sum + (r.minutos_trabajados || 0), 0);
  const diasConMinutos = registros.filter((r) => r.minutos_trabajados).length;

  // Celdas del calendario (lunes primero)
  const celdas = [];
  const primerDia = new Date(anio, mes - 1, 1);
  const diasMes = new Date(anio, mes, 0).getDate();
  const huecos = (primerDia.getDay() + 6) % 7;
  for (let i = 0; i < huecos; i += 1) celdas.push({ clave: `hueco-${i}`, vacia: true });
  const hoy = hoyMadrid();
  for (let dia = 1; dia <= diasMes; dia += 1) {
    const fechaStr = `${anio}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    const delDia = registros.filter((r) => r.fecha === fechaStr && r.hora_entrada);
    const minutos = delDia.reduce((s, r) => s + (r.minutos_trabajados || 0), 0);
    const enCurso = delDia.some((r) => !r.hora_salida);
    let clases = 'bg-gray-50 text-gray-400';
    let horas = null;
    if (delDia.length > 0) {
      if (enCurso) {
        clases = 'bg-blue-100 border-2 border-blue-400 text-blue-800';
      } else {
        const h = minutos / 60;
        if (h >= 8) clases = 'bg-green-100 border-2 border-green-400 text-green-800';
        else if (h >= 4) clases = 'bg-yellow-100 border-2 border-yellow-400 text-yellow-800';
        else clases = 'bg-orange-100 border-2 border-orange-400 text-orange-800';
        horas = h.toFixed(1);
      }
    }
    if (fechaStr === hoy) clases += ' ring-2 ring-duetto-primary';
    celdas.push({ clave: fechaStr, dia, clases, horas });
  }

  return (
    <div className="space-y-4" data-testid="registro-horario-empleado">
      <AvisoInline aviso={aviso} onCerrar={limpiarAviso} testId="registro-horario-aviso" />
      {errorCarga && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{errorCarga}</div>
      )}

      {/* Estadísticas del mes */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <h3 className="text-xs font-semibold text-blue-900 mb-1">Total horas</h3>
          <p className="text-lg sm:text-2xl font-bold text-blue-700">{(totalMinutos / 60).toFixed(1)}h</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <h3 className="text-xs font-semibold text-green-900 mb-1">Días trabajados</h3>
          <p className="text-lg sm:text-2xl font-bold text-green-700">{registros.filter((r) => r.hora_entrada).length}</p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
          <h3 className="text-xs font-semibold text-purple-900 mb-1">Promedio diario</h3>
          <p className="text-lg sm:text-2xl font-bold text-purple-700">
            {diasConMinutos > 0 ? (totalMinutos / 60 / diasConMinutos).toFixed(1) : '0.0'}h
          </p>
        </div>
      </div>

      {/* Calendario mensual */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4" data-testid="monthly-calendar">
        <div className="flex items-center justify-between mb-3">
          <button type="button" onClick={() => cambiarMes(-1)} className="p-2 hover:bg-gray-100 rounded-lg transition" title="Mes anterior">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h3 className="font-semibold text-gray-900 text-lg flex items-center gap-2 capitalize">
            <Calendar className="w-5 h-5 text-gray-500" />
            {new Date(anio, mes - 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
          </h3>
          <button type="button" onClick={() => cambiarMes(1)} className="p-2 hover:bg-gray-100 rounded-lg transition" title="Mes siguiente">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        {cargando ? (
          <div className="text-center py-8 text-gray-500">Cargando...</div>
        ) : (
          <div className="grid grid-cols-7 gap-1 sm:gap-2">
            {DIAS_SEMANA.map((d) => (
              <div key={d.clave} className="text-center text-xs font-bold text-gray-600 py-1">{d.letra}</div>
            ))}
            {celdas.map((c) => (c.vacia ? (
              <div key={c.clave} />
            ) : (
              <div
                key={c.clave}
                className={`${c.clases} text-center p-1 sm:p-2 min-w-0 rounded-lg`}
                title={c.horas ? `${c.horas}h trabajadas` : 'Sin registro'}
              >
                <div className="font-bold text-sm">{c.dia}</div>
                {c.horas && <div className="text-[10px] sm:text-xs mt-0.5 sm:mt-1 truncate">{c.horas}h</div>}
              </div>
            )))}
          </div>
        )}
        <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-xs">
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-green-100 border-2 border-green-400 rounded" />8h o más</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-yellow-100 border-2 border-yellow-400 rounded" />4-8h</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-orange-100 border-2 border-orange-400 rounded" />&lt;4h</span>
          <span className="flex items-center gap-1"><span className="w-4 h-4 bg-blue-100 border-2 border-blue-400 rounded" />En curso</span>
        </div>
      </div>

      {/* Historial detallado */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Historial detallado</h3>
          {puedeEditar && (
            <button
              type="button"
              onClick={() => abrirFormulario()}
              className="bg-duetto-primary hover:opacity-90 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition"
              data-testid="fichaje-agregar"
            >
              <Plus className="w-4 h-4" />
              <span>Agregar fichaje</span>
            </button>
          )}
        </div>
        {!cargando && registros.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No hay registros este mes</p>
        ) : (
          <div className="space-y-2">
            {registros.slice(0, 31).map((registro) => (
              <div key={registro.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3 hover:bg-gray-100 transition">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 capitalize">
                      {new Date(`${registro.fecha}T12:00:00`).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      <span className="font-medium">Entrada:</span> {horaCorta(registro.hora_entrada) || '-'}
                      {' · '}
                      <span className="font-medium">Salida:</span> {horaCorta(registro.hora_salida) || 'En curso'}
                    </p>
                    {registro.notas && <p className="text-xs text-gray-500 mt-1 italic">Nota: {registro.notas}</p>}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-bold text-xl text-duetto-primary">
                        {registro.minutos_trabajados ? (registro.minutos_trabajados / 60).toFixed(2) : '0.00'}h
                      </p>
                      <p className="text-xs text-gray-500 capitalize">{registro.tipo || 'normal'}</p>
                    </div>
                    {puedeEditar && (
                      <div className="flex flex-col gap-1">
                        <button
                          type="button"
                          onClick={() => abrirFormulario(registro)}
                          className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition"
                          title="Editar"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => { limpiarAviso(); setErrorBorrar(''); setABorrar(registro); }}
                          className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <DialogoConfirmar
        abierto={!!aBorrar}
        titulo="Eliminar fichaje"
        descripcion={aBorrar ? `Se eliminará el fichaje del ${aBorrar.fecha}. No se puede deshacer.` : ''}
        textoConfirmar="Eliminar"
        peligro
        ocupado={borrando}
        error={errorBorrar}
        onConfirmar={confirmarBorrado}
        onCancelar={() => { setABorrar(null); setErrorBorrar(''); }}
        testId="dialogo-borrar-fichaje"
      />

      {/* Formulario de alta/edición de fichaje */}
      {formAbierto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="bg-duetto-primary text-white px-6 py-4 rounded-t-lg">
              <h3 className="text-xl font-bold">{editando ? 'Editar fichaje' : 'Agregar fichaje'}</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
                <input
                  type="date"
                  value={form.fecha}
                  onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                  disabled={!!editando}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hora de entrada</label>
                <input
                  type="datetime-local"
                  value={form.hora_entrada}
                  onChange={(e) => setForm({ ...form, hora_entrada: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hora de salida</label>
                <input
                  type="datetime-local"
                  value={form.hora_salida}
                  onChange={(e) => setForm({ ...form, hora_salida: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas (opcional)</label>
                <textarea
                  value={form.notas}
                  onChange={(e) => setForm({ ...form, notas: e.target.value })}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                  placeholder="Ej: Fichaje manual por olvido"
                />
              </div>
              {errorForm && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm" role="alert">{errorForm}</div>
              )}
            </div>
            <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end gap-3">
              <button
                type="button"
                onClick={cerrarFormulario}
                disabled={guardando}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={guardarFichaje}
                disabled={guardando || !form.fecha}
                className="px-4 py-2 bg-duetto-primary text-white rounded-lg hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {guardando ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistroHorarioEmpleado;
